// Phase 30 / PTU-22 — register the `validationComplete` latch FIRST so the most
// recent event detail is captured even for a very early dispatch (a test harness
// or non-Angular caller can fire it before the lazy UploadPageComponent mounts).
// The detail is stored in sessionStorage (NOT a window property) so it survives
// the keycloak-js login-required full-page redirect round-trip — a window prop
// would be wiped by that reload. The upload page replays + clears it on init to
// surface the replaceRejected banner. Production-safe (no inline <script>).
window.addEventListener('validationComplete', (ev: Event) => {
  try {
    sessionStorage.setItem(
      '__nepf_lastValidationComplete',
      JSON.stringify((ev as CustomEvent).detail ?? null),
    );
  } catch {
    /* sessionStorage unavailable (private mode / SSR) — non-fatal */
  }
});

import { bootstrapApplication } from '@angular/platform-browser';
import { AppComponent } from './app/app.component';
import { buildAppConfig } from './app/app.config';
import { environment } from './environments/environment';

/**
 * Runtime configuration loader.
 *
 * The SPA bundle is environment-agnostic: every consumer of `environment` (Keycloak
 * init, API services, SignalR hub URL) reads the same exported object, but the
 * actual values for apiBase + keycloak.url come from /assets/config.json fetched
 * at first byte. The pipeline DeployDev/DeployTest stages write the real values
 * into wwwroot/assets/config.json from App Service settings, so the SAME built
 * bundle can ship to Dev, Test, UAT, and Prod without rebuilding.
 *
 * This avoids the build-time URL-baking that previously hardcoded
 * keycloak.dev.nde.gov into the bundle (Phase 01.1 — when NDE eventually stands
 * up their hostname we just rewrite config.json post-deploy, no rebuild).
 *
 * On fetch failure (missing or unparseable config.json) we fall back to whatever
 * `environment.ts` (or the file-replaced env file) already declared -- enough for
 * local `ng serve` to function against a localhost Keycloak/API.
 */
async function loadRuntimeConfig(): Promise<void> {
  try {
    const resp = await fetch('/assets/config.json', { cache: 'no-store' });
    if (!resp.ok) {
      // eslint-disable-next-line no-console
      console.warn(`[runtime-config] /assets/config.json returned HTTP ${resp.status}; using built-in defaults`);
      return;
    }
    const cfg = await resp.json();
    if (cfg.apiBase) environment.apiBase = cfg.apiBase;
    if (cfg.functionsBase) environment.functionsBase = cfg.functionsBase;
    if (cfg.keycloak) {
      Object.assign(environment.keycloak, cfg.keycloak);
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[runtime-config] /assets/config.json fetch failed; using built-in defaults', err);
  }
}

// Build appConfig AFTER loadRuntimeConfig has populated environment.* so the
// provideKeycloak() literal captures the production URL + clientId, not the
// empty defaults from environment.ts. Building appConfig as a const at the top
// of the module would freeze the empty values into the providers array before
// the runtime config fetch completes -- redirect URL would ship `client_id=`.
loadRuntimeConfig().then(() =>
  bootstrapApplication(AppComponent, buildAppConfig()).catch(err => console.error(err))
);
